/*
 * Authentication actions
 *
 * Actions change things in your application
 *
 * Since we use Redux (a uni-directional data flow), actions are the only way the application interacts with
 * your application state. This guarantees that your state is up to date and nobody
 * messes it up weirdly somewhere.
 *
 * To add a new Action:
 * 1) Create your constant
 * 2) Add a function like this:
 *    export function yourAction(var) {
 *        return { type: YOUR_ACTION_CONSTANT, var: var }
 *    }
 */

import { clearStorage } from './../persistence/storage';
import { clearSession } from './../persistence/session';

/** Login action constants */
export const LOGIN = 'DFIM_LOGIN';
export const LOGIN_SUCCESS = 'DFIM_LOGIN_SUCCESS';
export const LOGIN_ERROR = 'DFIM_LOGIN_ERROR';
/**
 * Login, this action starts the authentication epic
 * @return {object} An action object with a type of LOGIN
 */
export const login = (credentials, rememberMe) => ({ type: LOGIN, credentials, rememberMe });
/**
 * Dispatched when the login data is loaded by the authentication epic
 * @param  {object} login The login data
 * @return {object}      An action object with a type of
 *                       LOGIN_SUCCESS passing the login data
 */
export const loginSuccess = (data) => ({ type: LOGIN_SUCCESS, payload: data });
/**
 * Dispatched when the login fails
 * @param  {object} error The error
 * @return {object}       An action object with a type of LOGIN_ERROR passing the error
 */
export const loginError = (error) => ({ type: LOGIN_ERROR, error: error });

/** Logout action constants */
export const LOGOUT = 'DFIM_LOGOUT';
export const LOGOUT_SUCCESS = 'DFIM_LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'DFIM_LOGOUT_ERROR';
/**
 * Logout, this action starts the authentication epic
 * @return {object} An action object with a type of LOGOUT
 */
export const logout = () => ({ type: LOGOUT });
/**
 * Dispatched when the logout data is loaded by the authentication epic
 * @param  {array} logout The login data
 * @return {object}      An action object with a type of
 *                       LOGOUT_SUCCESS passing the login data
 */
export const logoutSuccess = () => {
  clearSession();
  clearStorage();
  return { type: LOGOUT_SUCCESS };
};
/**
 * Dispatched when the logout fails
 * @param  {object} error The error
 * @return {object}       An action object with a type of LOGOUT_ERROR passing the error
 */
export const logoutError = (error) => ({ type: LOGOUT_ERROR, error: error });

/** Get logged in user action constants */
export const GET_CURRENT_PRINCIPAL = 'DFIM_GET_CURRENT_PRINCIPAL';
export const GET_CURRENT_PRINCIPAL_SUCCESS = 'DFIM_GET_CURRENT_PRINCIPAL_SUCCESS';
export const GET_CURRENT_PRINCIPAL_ERROR = 'DFIM_GET_CURRENT_PRINCIPAL_ERROR';
/**
 * This starts the authentication epic
 * @return {object}       An action object with a type of GET_CURRENT_PRINCIPAL.
 *
 * */
export const getCurrentPrincipal = () => ({ type: GET_CURRENT_PRINCIPAL });
/**
 * Dispatched when the user is loaded
 * @param {object}       the principal loaded from the backend.
 * @return {object}       An action object with a type of GET_CURRENT_PRINCIPAL_SUCCESS passing the principal.
 *
 * */
export const getCurrentPrincipalSuccess = (data) => ({
  type: GET_CURRENT_PRINCIPAL_SUCCESS,
  payload: data,
});
/**
 * Dispatched when the user is loaded
 * @param  {object} error The error
 * @return {object}       An action object with a type of GET_CURRENT_PRINCIPAL_ERROR passing the error
 *
 * */
export const getCurrentPrincipalError = (error) => ({
  type: GET_CURRENT_PRINCIPAL_ERROR,
  error: error,
});

/** Get list of user roles action constants */
export const GET_USER_ROLES = 'DFIM_GET_USER_ROLES';
export const GET_USER_ROLES_SUCCESS = 'DFIM_GET_USER_ROLES_SUCCESS';
export const GET_USER_ROLES_ERROR = 'DFIM_GET_USER_ROLES_ERROR';
/**
 * This starts the authentication epic
 * @return {object}       An action object with a type of GET_USER_ROLES.
 *
 * */
export const getUserRoles = () => ({ type: GET_USER_ROLES });
/**
 * Dispatched when the user is loaded
 * @param {object}       the list of user roles loaded from the backend.
 * @return {object}       An action object with a type of GET_USER_ROLES_SUCCESS passing the list of user roles.
 *
 * */
export const getUserRolesSuccess = (data) => ({ type: GET_USER_ROLES_SUCCESS, payload: data });
/**
 * Dispatched when the user is loaded
 * @param  {object} error The error
 * @return {object}       An action object with a type of GET_USER_ROLES_ERROR passing the error
 *
 * */
export const getUserRolesError = (error) => ({ type: GET_USER_ROLES_ERROR, error: error });

/** Auto login action constants */
export const AUTO_LOGIN = 'DFIM_AUTO_LOGIN';
export const REMEMBER_ME = 'DFIM_AUTO_REMEMBER_ME';
export const FORGET_ME = 'DFIM_AUTO_FORGET_ME';
/**
 * Dispatched when the app is loaded
 * @return {object}      An action object with a type of
 *                       LOGOUT_SUCCESS passing the login data from local storage
 *                       or LOGOUT.
 */
export const autoLogin = () => ({ type: AUTO_LOGIN });
/**
 * Dispatched after login if the user checked 'remember me'
 * @param  {object} the received token confirming the user is authorized.
 * @return {object}       An action object with a type of REMEMBER_ME passing the token
 * */
export const rememberMe = (data) => ({ type: REMEMBER_ME, payload: data });
/**
 * Dispatched after logout.
 * @return {object}       An action object with a type of FORGET_ME
 * */
export const forgetMe = () => ({ type: FORGET_ME });
